import isEqual from "lodash/isEqual";

import type { Column, FillOperationParams } from "ag-grid-community";

/**
 * A row can carry values folded in from sibling rows the grid no longer shows,
 * keyed by sibling benefit and then by column field. The Quotes tab folds a
 * typed amount (a type plus its amount, cap, and similar) into the type row
 * this way, and onCellValueChanged saves the folded values with the cell.
 */
const COLLAPSED_SIBLINGS_KEY = "__collapsedSiblings";

type ValuesByColumn = Record<string, unknown>;
type CollapsedSiblings = Record<string, ValuesByColumn>;

interface CollapsedSiblingRow {
  benefitKey?: string;
  [COLLAPSED_SIBLINGS_KEY]?: CollapsedSiblings;
}

/**
 * The initial cell a horizontal fill copies into this target. AG Grid walks
 * the range outward from the initial cells and cycles through them (see
 * cycleFillValue), so each full cycle puts the source one more initial-range
 * width behind the target.
 */
const findHorizontalFillSource = ({
  api,
  column,
  currentIndex,
  direction,
  initialValues,
}: FillOperationParams): Column | undefined => {
  const isHorizontal = direction === "left" || direction === "right";
  if (!isHorizontal || initialValues.length === 0) return undefined;

  const width = initialValues.length;
  const offset = width * (Math.floor(currentIndex / width) + 1);
  const columns = api.getAllDisplayedColumns();
  const targetIndex = columns.indexOf(column);

  return columns[
    direction === "right" ? targetIndex - offset : targetIndex + offset
  ];
};

// A non-network row's folded values are keyed by the bare plan id after a
// grid rebuild, and by the edited sub-column after a popup edit. Read them the
// way the Quotes formatter does.
const readForColumn = (byColumn: ValuesByColumn, field: string): unknown =>
  field in byColumn ? byColumn[field] : byColumn[field.split(".")[0]];

/**
 * Copies the folded sibling values of the cell a fill copies from into the
 * target cell, so dragging "2 years" onto "24 months" carries the 2 and not
 * just the type. The row's own key, which the typed-amount popup mirrors into
 * the folded values, takes the filled value so the save doesn't write the old
 * type back.
 *
 * Runs from setFillValue, right before AG Grid writes the target, so the
 * change handler that write triggers saves the copied values too.
 */
export const copyCollapsedSiblingsOnFill = (
  params: FillOperationParams,
  fillValue: unknown,
): void => {
  const { column, rowNode } = params;
  const currentCellValue: unknown = params.currentCellValue;
  const row = rowNode.data as CollapsedSiblingRow | undefined;
  const siblings = row?.[COLLAPSED_SIBLINGS_KEY];
  if (!row || !siblings || !column.isCellEditable(rowNode)) return;

  const sourceField = findHorizontalFillSource(params)?.getColDef().field;
  const targetField = column.getColDef().field;
  if (!sourceField || !targetField) return;

  const isOwnKey = (key: string): boolean => key === row.benefitKey;
  const copied = Object.fromEntries(
    Object.entries(siblings).map(([key, byColumn]) => [
      key,
      isOwnKey(key) ? fillValue : readForColumn(byColumn, sourceField),
    ]),
  );
  const differs = (key: string): boolean =>
    !isEqual(readForColumn(siblings[key], targetField), copied[key]);
  const siblingKeys = Object.keys(copied);
  if (!siblingKeys.some(differs)) return;

  row[COLLAPSED_SIBLINGS_KEY] = Object.fromEntries(
    Object.entries(siblings).map(([key, byColumn]) => [
      key,
      { ...byColumn, [targetField]: structuredClone(copied[key]) },
    ]),
  );

  // AG Grid only writes a fill target whose current value differs from the
  // fill value, so 2 years dragged onto 3 years would leave the copied amount
  // unsaved and unpainted. Write the cell here; AG Grid then finds the values
  // equal and skips its own write.
  const onlyFoldedValuesDiffer =
    currentCellValue === fillValue &&
    siblingKeys.some((key) => !isOwnKey(key) && differs(key));
  if (onlyFoldedValuesDiffer) {
    rowNode.setDataValue(column, fillValue, "rangeSvc");
  }
};
